/**
 * Chat Action Card Registry.
 *
 * Maps actionType → { PreviewCard, ResultCard }.
 *
 * PreviewCard — rendered before the user confirms (rich preview).
 * ResultCard  — rendered after execution (deeplink, summary).
 *
 * Adding a new action type requires only:
 *   1. Create domain/<Action>Preview.jsx + <Action>Result.jsx
 *   2. Register below: ACTION_NAME: { PreviewCard, ResultCard }
 *   No other files need to change.
 *
 * Note: ResultCard is mandatory only if the tool emits actionResult.
 * PreviewCard is mandatory only if the tool is two-step (pendingAction).
 * Tools that are read-only / plan can register only ResultCard.
 */

import CreateInvoicePreview from './invoice/CreateInvoicePreview';
import CreateInvoiceResult  from './invoice/CreateInvoiceResult';
import DetectInvoicePatternsResult from './invoice/DetectInvoicePatternsResult';
import VerifactuValidationResult   from './invoice/VerifactuValidationResult';
import CreateExpensePreview        from './expense/CreateExpensePreview';
import DetectExpensePatternsResult from './expense/DetectExpensePatternsResult';
import ExpenseDeductibilityResult  from './expense/ExpenseDeductibilityResult';
import JournalEntryValidationResult    from './accounting/JournalEntryValidationResult';
import JournalEntryPreview             from './accounting/JournalEntryPreview';
import DepreciationBatchPreview        from './accounting/DepreciationBatchPreview';
import ReplayClosingPreview            from './accounting/ReplayClosingPreview';
import DetectAccountingPatternsResult  from './accounting/DetectAccountingPatternsResult';
import CollectionCyclePreview from './collection/CollectionCyclePreview';
import CollectionCycleResult  from './collection/CollectionCycleResult';
import EmitDraftsPreview from './drafts/EmitDraftsPreview';
import EmitDraftsResult  from './drafts/EmitDraftsResult';
import CalculatePayrollPreview from './payroll/CalculatePayrollPreview';
import CalculatePayrollResult  from './payroll/CalculatePayrollResult';
import SimulateBatchResult     from './payroll/SimulateBatchResult';
import ApplyGroupOverrideResult from './payroll/ApplyGroupOverrideResult';
import AutoResolvePreview from './payroll/AutoResolvePreview';
import AutoResolveResult  from './payroll/AutoResolveResult';
import ProposeSalaryPreview from './payroll/ProposeSalaryPreview';
import ProposeSalaryResult  from './payroll/ProposeSalaryResult';
import DetectPatternsResult from './payroll/DetectPatternsResult';
import UpdateEmployeePreview from './employee/UpdateEmployeePreview';
import UpdateEmployeeResult  from './employee/UpdateEmployeeResult';
import CreateQuotePreview from './crm/CreateQuotePreview';

export const ACTION_REGISTRY = {
  // Invoicing
  CREATE_INVOICE: {
    PreviewCard: CreateInvoicePreview,
    ResultCard:  CreateInvoiceResult,
  },
  BULK_REMINDERS: {
    PreviewCard: CollectionCyclePreview,
    ResultCard:  CollectionCycleResult,
  },
  EMIT_DRAFTS: {
    PreviewCard: EmitDraftsPreview,
    ResultCard:  EmitDraftsResult,
  },
  DETECT_SUSPICIOUS_INVOICES: {
    // Read-only audit — no preview, just result card
    ResultCard:  DetectInvoicePatternsResult,
  },
  VALIDATE_INVOICE_VERIFACTU: {
    // Read-only validation — no preview, just result card
    ResultCard:  VerifactuValidationResult,
  },

  // Expenses
  CREATE_EXPENSE: {
    // Two-step expense registration — base/IVA/IRPF breakdown + deductibility warning
    PreviewCard: CreateExpensePreview,
  },
  DETECT_SUSPICIOUS_EXPENSES: {
    // Read-only audit — no preview, just result card
    ResultCard:  DetectExpensePatternsResult,
  },
  VALIDATE_EXPENSE_DEDUCTIBILITY: {
    // Read-only validation — no preview, just result card
    ResultCard:  ExpenseDeductibilityResult,
  },

  // Accounting (asientos) — all asiento posts share JournalEntryPreview
  // (Debe/Haber table). CREATE_JOURNAL_ENTRY uses preview.lineas (string
  // amounts); the contable posts below nest preview.asiento.lineas (numeric).
  CREATE_JOURNAL_ENTRY: {
    // Two-step manual journal entry — Debe/Haber preview before posting
    PreviewCard: JournalEntryPreview,
  },
  POST_PERIODIFICATION:  { PreviewCard: JournalEntryPreview },
  POST_AGING_PROVISION:  { PreviewCard: JournalEntryPreview },
  RECLASSIFY_LEASING:    { PreviewCard: JournalEntryPreview },
  APPLY_CUSTOMER_ADVANCE:{ PreviewCard: JournalEntryPreview },
  APPLY_SUPPLIER_ADVANCE:{ PreviewCard: JournalEntryPreview },
  POST_TAX_PROVISION:    { PreviewCard: JournalEntryPreview },
  // Batch posts with their own (non-asiento) shape — dedicated cards so the
  // nested activos/plantillas arrays survive (the generic card drops them).
  POST_DEPRECIATION_BATCH: { PreviewCard: DepreciationBatchPreview },
  REPLAY_CLOSING:          { PreviewCard: ReplayClosingPreview },
  VALIDATE_JOURNAL_ENTRY: {
    // Read-only validation — no preview, just result card
    ResultCard:  JournalEntryValidationResult,
  },
  DETECT_SUSPICIOUS_ACCOUNTING: {
    // Read-only audit — no preview, just result card
    ResultCard:  DetectAccountingPatternsResult,
  },

  // Payroll / HR
  CALCULATE_PAYROLL: {
    PreviewCard: CalculatePayrollPreview,
    ResultCard:  CalculatePayrollResult,
  },
  SIMULATE_BATCH_PAYROLL: {
    // Read-only simulation — no preview, just result card
    ResultCard:  SimulateBatchResult,
  },
  APPLY_GROUP_OVERRIDE: {
    // Returns a plan — no preview, just result card
    ResultCard:  ApplyGroupOverrideResult,
  },
  AUTO_RESOLVE_PAYROLL: {
    PreviewCard: AutoResolvePreview,
    ResultCard:  AutoResolveResult,
  },
  PROPOSE_SALARY_INCREASE: {
    PreviewCard: ProposeSalaryPreview,
    ResultCard:  ProposeSalaryResult,
  },
  DETECT_SUSPICIOUS_PATTERNS: {
    // Read-only audit — no preview, just result
    ResultCard:  DetectPatternsResult,
  },
  UPDATE_EMPLOYEE: {
    PreviewCard: UpdateEmployeePreview,
    ResultCard:  UpdateEmployeeResult,
  },

  // CRM
  CREATE_QUOTE: {
    // Two-step quote — line-items table + subtotal/IVA/total before creating.
    // actionResult is sparse ({type,id}) so no ResultCard; the assistant's
    // message confirms creation. The other CRM writes (contact/opportunity)
    // stay on the generic card — their previews are flat key/value.
    PreviewCard: CreateQuotePreview,
  },
};
